import type { OperationTypeNode } from 'graphql';
import type { SsrTimingCollector } from './ssrTimingCollector';
import { getOperationAST } from 'graphql';
import { RetryLink } from '@apollo/client/link/retry';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import fetchMethod from 'isomorphic-fetch';
import possibleTypesResultData from '@graphql/generated-base';
import { getTerminatingLink as clientDiagnosticsTerminatingLink } from './clientDiagnostics';
import globalWindow from '@shared/core/globals';
import type { LoginManager } from '../auth';
import type { Operation, TypePolicies } from '@apollo/client';
import { ApolloClient, ApolloLink, HttpLink, Observable, from, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { catalogCachePolicies } from '@apps/registry/common/components/Catalog/Catalog.cache';
import { travelCachePolicies } from './cachePolicies/travelCachePolicy';
import { v4 as uuid } from 'uuid';
import AbortController from 'abort-controller';
import type { IncomingHttpHeaders } from 'http';
import { logGQLTelemetry } from './logGQLTelemetry';
import { cardShopCachePolicies } from '@apps/card/routes/Dashboard/components/DesignsGallery/DesignsGallery.cache';
import { guestCachePolicies } from './cachePolicies/guestCachePolicy';
import { adminCachePolicies } from './cachePolicies/adminCachePolicy';
import { createOperationAwareUri } from './createOperationAwareUri';
export interface ClientArgs {
  graphQLUrl: string;
  loginManager: LoginManager;

  overrides?: {
    cache?: InMemoryCache;
  };
  ssrAuthToken?: string;
  headers?: IncomingHttpHeaders;
  isAdminRsvpClient?: boolean;
  ssrTimingCollector?: SsrTimingCollector;
}

export interface PersistentStorage<T> {
  getItem: (key: string) => Promise<T> | T;
  setItem: (key: string, data: T) => Promise<void> | void;
  removeItem: (key: string) => Promise<void> | void;
}

function shouldConsiderNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Browser: all network failures throw TypeError per the Fetch API spec (desktop + mobile).
  if (error instanceof TypeError) return true;
  // Node.js SSR via isomorphic-fetch/node-fetch throws FetchError, not TypeError.
  // type === 'system' is set for actual network failures (ECONNREFUSED, ENOTFOUND, etc.).
  if (error.name === 'FetchError' && (error as Error & { type?: string }).type === 'system') return true;
  return false;
}

// Apollo sets error.name = 'ServerParseError' and attaches statusCode when JSON.parse() fails on a 5xx response.
function shouldConsiderServerParseError(error: unknown): boolean {
  return error instanceof Error && error.name === 'ServerParseError' && ((error as Error & { statusCode?: number }).statusCode ?? 0) >= 500;
}

/**
 * At the root network layer, we only want to retry read + subscription operations.
 * The app layer has the control to retry based upon the context of the code.
 */
function shouldRetryOperation(operationType: Maybe<OperationTypeNode>): boolean {
  return operationType === 'query' || operationType === 'subscription';
}

function understandOperation({ query, operationName }: Operation): { shouldRetry: boolean } {
  // When a request is made, GraphQL combines the query document with the schema definition (schema.graphql)
  // and converts it into a tree structure.
  //
  // A query document can contain multiple `OperationDefinition` - one of many node types in the GraphQL AST.
  //
  // There are 3 types of executable operations: query, mutation, and subscription.

  // Find the appropriate operation.
  const match = getOperationAST(query, operationName);

  return { shouldRetry: shouldRetryOperation(match?.operation) };
}

const getTerminatingLink = (url: string, fetch: typeof fetchMethod) => {
  const { resolve: resolveUri } = createOperationAwareUri(url);

  return new HttpLink({
    credentials: 'include',
    uri: resolveUri,
    fetch,
  });
};

const createAuthLink = ({ loginManager, ssrAuthToken, headers = {}, isAdminRsvpClient = false }: ClientArgs): ApolloLink => {
  const abortController = new AbortController();
  const createAuthContext = (accessToken: string | null) => {
    let token = accessToken;
    if (!token && ssrAuthToken) {
      token = ssrAuthToken;
    }

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        ...(isAdminRsvpClient ? {} : { 'x-joy-request-id': uuid() }),
      },
      // to prevent this issue: https://github.com/apollographql/apollo-client/issues/6769
      fetchOptions: {
        signal: abortController.signal,
      },
    };
  };
  return setContext(async _op => {
    const token = await loginManager.getTokenAsync();
    return createAuthContext(token);
  });
};

export const createApolloCache = () => {
  const typePolicies = {
    ...adminCachePolicies,
    ...cardShopCachePolicies,
    ...catalogCachePolicies,
    ...guestCachePolicies,
    ...travelCachePolicies,
    AccommodationPlacePricingAndBookingDetails: {
      keyFields: ['accommodationPlace', ['id']], // Use `accommodationPlace.id` as part of the cache key
    },
    JoyPlacePricingAndBookingDetails: {
      keyFields: ['joyPlace', ['id']], // Use `joyPlace.id` as part of the cache key
    },
    UserConsent: {
      keyFields: ['eventId', 'consentKey'], // Use `eventId` and `consentKey` as part of the cache key
    },
    Query: {
      fields: {
        ...travelCachePolicies.Query.fields,
        stationeryTemplatesContainer: {
          keyArgs: ['filter', 'includeOptions', 'first'],
        },
        // Ensure unique cache entry by including the `query` as part of the generated key
        productCatalogSearchResults: {
          keyArgs: ['query'],
        },
        // Ensure unique cache entry by including the `eventId` as part of the generated key
        recommendCatalogHomeSections: {
          keyArgs: ['eventId', 'storefront'],
        },
        // Ensure eventById will always return a reference to the event
        eventById(_, { args, toReference }) {
          return toReference({
            __typename: 'Event',
            __ref: args?.id,
          });
        },
        userConsentKeyForEvent: {
          keyArgs: ['eventId', 'consentKey'],
          read(existing, { args, toReference }) {
            if (existing) return existing;

            // Create a synthetic reference (will auto-link if real object is added later)
            // This is useful when the key doesn't exist on the server yet.
            return toReference({
              __typename: 'UserConsent',
              eventId: args?.eventId,
              consentKey: args?.consentKey,
            });
          },
        },
      },
    },
  } as TypePolicies;

  return new InMemoryCache({
    possibleTypes: possibleTypesResultData.possibleTypes,
    typePolicies,
  }).restore(globalWindow.__INITIAL_APOLLO_STATE__ || {});
};

export const createApolloClient = (args: ClientArgs) => {
  const { graphQLUrl, overrides, isAdminRsvpClient = false, ssrTimingCollector } = args;
  const cache = overrides?.cache || createApolloCache();
  const authLink = createAuthLink(args);

  // Records GQL operation timing for SSR observability.
  // Subscribes to both success and error paths so failing/slow operations
  // are still captured in phase breakdowns and NR attributes.
  // Note: this link sits before retryLink in the chain, so the recorded
  // duration includes all retry attempts — this is intentional for wall-clock accuracy.
  const ssrTimingLink = ssrTimingCollector
    ? new ApolloLink((operation, forward) => {
        const startTime = performance.now();
        return new Observable(observer => {
          const subscription = forward(operation).subscribe({
            next: data => {
              observer.next(data);
            },
            error: err => {
              ssrTimingCollector.record(operation.operationName || 'unknown', startTime, performance.now());
              observer.error(err);
            },
            complete: () => {
              ssrTimingCollector.record(operation.operationName || 'unknown', startTime, performance.now());
              observer.complete();
            },
          });
          return () => subscription.unsubscribe();
        });
      })
    : null;
  const errorLoggingLink = onError(({ networkError, graphQLErrors, operation }: ErrorResponse) => {
    logGQLTelemetry({
      name: operation.operationName,
      startTimeMs: operation.getContext().startTime,
      status: networkError ? 'NetworkError' : graphQLErrors ? 'GraphQLError' : 'UnknownError',
      errors: [...(networkError ? [networkError] : []), ...(graphQLErrors || [])],
    });
  });

  const successLoggingLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      startTime: performance.now(),
    });

    return forward(operation).map(data => {
      logGQLTelemetry({
        name: operation.operationName,
        startTimeMs: operation.getContext().startTime,
        status: 'Success',
      });
      return data;
    });
  });

  // The retry link does not handle retries for GraphQL errors in the response, only for network errors.
  // This means that the entire query was rejected, and therefore no data was returned.
  const retryLink = new RetryLink({
    delay: {
      // The number of milliseconds to wait before attempting the first retry
      initial: 300,
      // The maximum number of milliseconds that the link should wait for any retry
      // @default Infinity
      max: 2000,
      // Whether delays between attempts should be randomized.
      // Combined with initial, this will help alleviate the thundering herd problem.
      jitter: true,
    },

    attempts: {
      max: 5,
      retryIf: (error, operation) => {
        if (shouldConsiderNetworkError(error) || shouldConsiderServerParseError(error)) {
          return understandOperation(operation).shouldRetry;
        }
        return false;
      },
    },
  });

  const terminatingLink = globalWindow.location?.search.includes('feature.')
    ? clientDiagnosticsTerminatingLink(graphQLUrl, getTerminatingLink)
    : getTerminatingLink(graphQLUrl, fetchMethod);

  return new ApolloClient({
    connectToDevTools: __BROWSER__,
    cache,
    // Ordering of links matter.
    // - authLink goes before retry + error links so that auth headers are set prior to any HTTP requests.
    // - retryLink should be before HTTP link so that it can retry specific operations before logging it as an error.
    // Removing createEnforceWafCaptchaLink(graphQLUrl) to avoid WAF captcha. Will be added when needed.
    link: from([authLink, ssrTimingLink, successLoggingLink, errorLoggingLink, retryLink, terminatingLink].filter(Boolean) as ApolloLink[]),
    queryDeduplication: true,
    ssrMode: true,
    name: isAdminRsvpClient ? undefined : 'joy-web',
    version: isAdminRsvpClient ? undefined : process.env.APOLLO_CLIENT_VERSION, // comes from webpack.DefinePlugin
  });
};
